import _ from 'lodash';
import Mapper from '../utils/Mapper';
import Products from '../utils/Products.js';
import {YesNoValues} from '../constants/ApplicationConstants.js';
import AssetAllocationConstants from '../constants/AssetAllocationConstants';
import InSpecieTransferConstants from '../constants/InSpecieTransferConstants.js';
import ApplicationHelper from '../utils/ApplicationHelper.js';

function _getInvestmentOptionsWithAmount(totalAmount, investmentOptions) {
    const investmentOptionsWithAmount = _.map(investmentOptions, function(invOpt) {
        const weight = parseFloat(invOpt.weight);
        const weightedTotalRolloverAmt = totalAmount * (weight / 100);
        return _.assign(invOpt, {
            balance: weightedTotalRolloverAmt,
            specifiedAmount: weightedTotalRolloverAmt,
            transactionAmount: weightedTotalRolloverAmt,
            weight: weight
        });
    });
    return investmentOptionsWithAmount;
}

function _getAssetAllocationWithDisplayNames(investmentAllocations) {
    const assetAllocations = _.uniq(investmentAllocations,
      function(a) {
          return a.assetAllocationCode.concat(a.investmentOptionId);
      });

    let assetAllocationWithDisplayNames = _.sortBy(assetAllocations, function(a) {
        return (AssetAllocationConstants.AssetClassCodeToOrder[a.assetAllocationCode].sortOrder);
    });

    _.forEach(assetAllocationWithDisplayNames,
        function(a) {
            _.assign(a,
                { displayName: AssetAllocationConstants.AssetClassCodeToOrder[a.assetAllocationCode].displayName });
        });
    return assetAllocationWithDisplayNames;
}

function _getConsolidatedDataForNonPension(totalAmount, investmentOptions, investmentAllocations) {
    const consolidatedInSpecieOptions = {
        isLikeForLikeTransfer: false,
        useAllocationForTransfer: false,
        isInSpecieWithTermDeposit: false,
        hasManuallyEnteredInSpecie: false,
        accountFundingSources: []
    }

    const consolidatedInvestmentOptions = _getInvestmentOptionsWithAmount(totalAmount, investmentOptions);

    const consolidatedAssetAllocations = _getAssetAllocationWithDisplayNames(investmentAllocations);

    return {
        consolidatedInSpecieOptions: consolidatedInSpecieOptions,
        consolidatedInvestmentOptions: consolidatedInvestmentOptions,
        consolidatedAssetAllocations: consolidatedAssetAllocations,
        investmentOptionsExternalOnly: consolidatedInvestmentOptions,
        assetAllocationsExternalOnly: consolidatedAssetAllocations
    }
}

function _getConsldInSpecieOptions(inSpecieTransfer) {
    // return consolidatedInSpecieOptions which will be used to determine if certain messages/table/pie chart will show
    const isLikeForLikeTransfer = inSpecieTransfer.setUp === YesNoValues.YES &&
        inSpecieTransfer.externalAccountQuestion !== YesNoValues.NO;
    const useAllocationForTransfer = inSpecieTransfer.setUp === YesNoValues.YES &&
        inSpecieTransfer.externalAccountQuestion === YesNoValues.NO;
    const investmentOptionsForInSpecie = inSpecieTransfer.inSpecieAccounts.length > 0 ?
        _.flatten(_.map(inSpecieTransfer.inSpecieAccounts, 'investmentOptions')) : [];
    const isInSpecieWithTermDeposit = _.some(investmentOptionsForInSpecie, function(i) {
        return i.termDeposit === true && Mapper.toCurrency(i.balance) > 0
    });

    let accountFundingSources = [];
    const isInternalTermDepositOnly = investmentOptionsForInSpecie.length > 0 &&
        _.some(investmentOptionsForInSpecie,
            function(i) {
                return i.termDeposit === true && Mapper.toCurrency(i.balance) > 0
            }) &&
        !_.some(investmentOptionsForInSpecie,
            function(i) {
                return i.termDeposit !== true && Mapper.toCurrency(i.balance) > 0
            });
    if (isInternalTermDepositOnly) {
        accountFundingSources.push(InSpecieTransferConstants.FundingSources.INTERNAL_TD_ONLY);
    } else {
        const isInternalNonTermDepositOnly = investmentOptionsForInSpecie.length > 0 &&
            _.all(investmentOptionsForInSpecie,
                function(i) {
                    return i.termDeposit !== true || (i.termDeposit === true && Mapper.toCurrency(i.balance) <= 0)
                });
        if (isInternalNonTermDepositOnly) {
            accountFundingSources.push(InSpecieTransferConstants.FundingSources.INTERNAL_NO_TD);
        } else {
            const isInternalTermDepositAndNonTermDeposit = investmentOptionsForInSpecie.length > 0 &&
                _.some(investmentOptionsForInSpecie,
                    function(i) {
                        return i.termDeposit === true && Mapper.toCurrency(i.balance) > 0
                    }) &&
                _.some(investmentOptionsForInSpecie,
                    function(i) {
                        return (i.termDeposit !== true && Mapper.toCurrency(i.balance) > 0)
                    });
            if (isInternalTermDepositAndNonTermDeposit) {
                accountFundingSources.push(InSpecieTransferConstants.FundingSources.INTERNAL_TD_AND_NON_TD);
            }
        }
    }

    const hasManuallyEnteredInSpecie = _.some(inSpecieTransfer.inSpecieAccounts, function(a) {
        return a.isExistingAccount === false;
    });
    if (hasManuallyEnteredInSpecie) {
        accountFundingSources.push(InSpecieTransferConstants.FundingSources.INTERNAL_MANUAL);
    }
    
    const externalRollOverExists = (inSpecieTransfer.externalFunds && inSpecieTransfer.externalFunds.length > 0);
    const internalIneligibleForInspecieExists = (inSpecieTransfer.internalNonEligibleAccounts && inSpecieTransfer.internalNonEligibleAccounts.length > 0);
    if (externalRollOverExists ||internalIneligibleForInspecieExists ) {
        accountFundingSources.push(InSpecieTransferConstants.FundingSources.EXTERNAL);
    }
    
    const consolidatedInSpecieOptions = {
        isLikeForLikeTransfer: isLikeForLikeTransfer,
        useAllocationForTransfer: useAllocationForTransfer,
        isInSpecieWithTermDeposit: isInSpecieWithTermDeposit,
        hasManuallyEnteredInSpecie: hasManuallyEnteredInSpecie,
        accountFundingSources: accountFundingSources
    };
    return consolidatedInSpecieOptions;
}

function _getConsldInvUsingInvAllocationOnly(invAllocationOptions, totalRollOverAmount) {
    let consolidatedInvestmentOptions = [];
    _.forEach(invAllocationOptions,
        function(invAllocationOption) {
            let invAllocationOptionWithAmt = _.cloneDeep(invAllocationOption);
            const weight = parseFloat(invAllocationOption.weight);
            const weightedExtTotalRolloverAmt = weight / 100 * totalRollOverAmount;
            _.assign(invAllocationOptionWithAmt, {
                balance: weightedExtTotalRolloverAmt,
                specifiedAmount: weightedExtTotalRolloverAmt,
                transactionAmount: weightedExtTotalRolloverAmt,
                weight: weight
            });
            consolidatedInvestmentOptions.push(invAllocationOptionWithAmt);
        });
    return consolidatedInvestmentOptions;
}

function _getInSpecieInvestmentOptions(inSpecieAccounts) {
    // get the InSpecie Accounts' Investment Options list, each item of the list has total Rollover Amount
    let inSpecieInvOptions = [];
    _.forEach(inSpecieAccounts,
        function (account) {
            const accountRollOverRatio = Mapper.toCurrency(account.rollOverAmount) /
                (Mapper.toCurrency(account.rollOverAmount) + Mapper.toCurrency(account.remainingBalance));

            _.forEach(account.investmentOptions, function (invOption) {
                const invOptionBalance = Mapper.toCurrency(invOption.balance) * accountRollOverRatio;
                const invOptionSpecifiedAmount = invOption.specifiedAmount * accountRollOverRatio;
                const invOptionTransactionAmount = invOption.transactionAmount * accountRollOverRatio;

                if (invOptionBalance > 0) {
                    const idx = _.findIndex(inSpecieInvOptions, { id: invOption.id });
                    // accumulate the rollover amount
                    if (idx < 0) {
                        invOption.balance = invOptionBalance;
                        invOption.specifiedAmount = invOptionSpecifiedAmount;
                        invOption.transactionAmount = invOptionTransactionAmount;
                        inSpecieInvOptions.push(invOption);
                    } else {
                        inSpecieInvOptions[idx].balance = inSpecieInvOptions[idx].balance + invOptionBalance;
                        inSpecieInvOptions[idx].specifiedAmount = inSpecieInvOptions[idx].specifiedAmount + invOptionSpecifiedAmount;
                        inSpecieInvOptions[idx].transactionAmount = inSpecieInvOptions[idx].transactionAmount + invOptionTransactionAmount;
                    }
                }
            });
        });
    return inSpecieInvOptions;
}

function _getConsldInvLikeForLike(inSpecieInvestmentOptions, inSpecieTotalRolloverAmt, externalTotalRolloverAmt) {
    let consolidatedInvestmentOptions = [];
    _.forEach(inSpecieInvestmentOptions,
        function(inSpecieInvOption) {
            let invOption = _.cloneDeep(inSpecieInvOption);
            const ratio = invOption.balance / inSpecieTotalRolloverAmt;
            const weightedExtTotalRolloverAmt = externalTotalRolloverAmt * ratio;
            _.assign(invOption,
            {
                balance: inSpecieInvOption.balance + weightedExtTotalRolloverAmt,
                specifiedAmount: inSpecieInvOption.specifiedAmount + weightedExtTotalRolloverAmt,
                transactionAmount: inSpecieInvOption.transactionAmount + weightedExtTotalRolloverAmt,
                weight: ratio * 100
            });
            consolidatedInvestmentOptions.push(invOption);
        });
    return consolidatedInvestmentOptions;
}

function _getConsldInvInspecieAndAlloc(inSpecieInvestmentOptions, invAllocationOptions, externalTotalRolloverAmt) {
    let consolidatedInvestmentOptions = _.cloneDeep(inSpecieInvestmentOptions);
    _.forEach(invAllocationOptions,
        function(invAllocationOption) {
            const existingIdx = _.findIndex(consolidatedInvestmentOptions, { id: invAllocationOption.id });
            if (existingIdx < 0) {
                let invAllocationOptionWithAmt = _.cloneDeep(invAllocationOption);
                const weightedExtTotalRolloverAmt = parseFloat(invAllocationOption.weight) / 100 * externalTotalRolloverAmt;
                _.assign(invAllocationOptionWithAmt, {
                    balance: weightedExtTotalRolloverAmt,
                    specifiedAmount: weightedExtTotalRolloverAmt,
                    transactionAmount: weightedExtTotalRolloverAmt
                });
                consolidatedInvestmentOptions.push(invAllocationOptionWithAmt);
            } else {
                const weightedExternalFundTotal = parseFloat(invAllocationOption.weight) / 100 * externalTotalRolloverAmt;
                consolidatedInvestmentOptions[existingIdx].balance =
                    consolidatedInvestmentOptions[existingIdx].balance + weightedExternalFundTotal;
                consolidatedInvestmentOptions[existingIdx].specifiedAmount =
                    consolidatedInvestmentOptions[existingIdx].specifiedAmount + weightedExternalFundTotal;
                consolidatedInvestmentOptions[existingIdx].transactionAmount =
                    consolidatedInvestmentOptions[existingIdx].transactionAmount + weightedExternalFundTotal;
            }
        });

    const totalBalance = _.sum(consolidatedInvestmentOptions, 'balance');
    _.forEach(consolidatedInvestmentOptions,
        function(consolidatedInvOpt) {
            const weight = (consolidatedInvOpt.balance / totalBalance) * 100;
            _.assign(consolidatedInvOpt, { weight: weight });
        });
    return consolidatedInvestmentOptions;
}

function _getConsolidatedDataForPension(inSpecieTransfer, investmentAllocation, forSubmit) {
    const consolidatedInSpecieOptions = _getConsldInSpecieOptions(inSpecieTransfer);

    // invAllocationOptions: the investment allocation options specified by the user in the Investment Allocation table
    const invAllocationOptions = _.cloneDeep(investmentAllocation.investmentOptions);

    // the funding source: there will be 3 types of funding: internal inspecie, internal but not eligible inspecie, external
    const inSpecieAccounts = _.cloneDeep(_.get(inSpecieTransfer, 'inSpecieAccounts', []));
    const externalAccounts = _.cloneDeep(_.get(inSpecieTransfer, 'externalFunds', []));
    const internalNonEligibleAccounts = _.cloneDeep(_.get(inSpecieTransfer, 'internalNonEligibleAccounts', []));

    // calculate the rollover total of external funds (which include external accounts, and internal but not eligible inspecie)
    const extFundsTotalRolloverAmt = _.sum(externalAccounts,
        function(externalFund) {
            return Mapper.toCurrency(externalFund.rollOverAmount);
        });
    const intNonEligibleTotalRolloverAmt = _.sum(internalNonEligibleAccounts,
        function(internalNonEligibleAccount) {
            return Mapper.toCurrency(internalNonEligibleAccount.rollOverAmount);
        });
    const externalTotalRolloverAmt = extFundsTotalRolloverAmt + intNonEligibleTotalRolloverAmt;

    // calculate the rollover total of internal inspecie accounts
    const inSpecieTotalRolloverAmt = _.sum(inSpecieAccounts,
        function(account) {
            return Mapper.toCurrency(account.rollOverAmount);
        });

    // get list of unique investment options in inspecie accounts
    let inSpecieInvestmentOptions = _getInSpecieInvestmentOptions(inSpecieAccounts);

    // the conditions required to determine scenarios to calculate the investment options
    // 3 cases:
    // 1. use investment allocations specified by users in investment allocation table only
    // 2. use inspecie accounts only to do like for like transfer
    // 3. use both inspecie accounts and investment allocations
    const hasInSpecieAccountsOnly = externalAccounts.length === 0 &&
        internalNonEligibleAccounts.length === 0 && inSpecieAccounts.length > 0;
    const hasBothExtAndInSpecie = (externalAccounts.length > 0 ||
        internalNonEligibleAccounts.length > 0) && inSpecieAccounts.length > 0;
    const hasExternalAccountsOnly = (externalAccounts.length > 0 ||
        internalNonEligibleAccounts.length > 0) && inSpecieAccounts.length === 0;
    const setUp = _.get(inSpecieTransfer, 'setUp', null);
    const extAcctQuestion = _.get(inSpecieTransfer, 'externalAccountQuestion', null);
    const likeForLikeTransfer = (hasInSpecieAccountsOnly && setUp === YesNoValues.YES) ||
        (hasBothExtAndInSpecie && setUp === YesNoValues.YES && extAcctQuestion === YesNoValues.YES);
    const noInspecieSetup = setUp === YesNoValues.NO;
    const noIntAllocDueToManuallyEntered = consolidatedInSpecieOptions.useAllocationForTransfer &&
            consolidatedInSpecieOptions.hasManuallyEnteredInSpecie;
    const noIntAllocDueToTermDep = consolidatedInSpecieOptions.useAllocationForTransfer &&
            consolidatedInSpecieOptions.isInSpecieWithTermDeposit;
    const useInvAllocationsOnly = noInspecieSetup || hasExternalAccountsOnly || noIntAllocDueToManuallyEntered || noIntAllocDueToTermDep;

    // consolidate investment options amounts and asset allocation
    let assetAllocations = [];
    let consolidatedInvestmentOptions = null;
    if (useInvAllocationsOnly) {
        // Case 1: use Investment Allocation only
        assetAllocations = _.uniq(investmentAllocation.assetAllocations,
            function(a) {
                return a.assetAllocationCode.concat(a.investmentOptionId);
            });
        let amount = 0;
        if (noIntAllocDueToManuallyEntered || noIntAllocDueToTermDep) {
            amount = externalTotalRolloverAmt;
        } else {
            amount = externalTotalRolloverAmt + inSpecieTotalRolloverAmt;
        }
        consolidatedInvestmentOptions = _getConsldInvUsingInvAllocationOnly(invAllocationOptions, amount);
    } else if (likeForLikeTransfer) {
        // Case 2: use all inspecie
        assetAllocations = _.uniq(investmentAllocation.inSpecieAssetAllocations,
            function(a) {
                return a.assetAllocationCode.concat(a.investmentOptionId);
            });
        consolidatedInvestmentOptions = _getConsldInvLikeForLike(inSpecieInvestmentOptions, inSpecieTotalRolloverAmt, externalTotalRolloverAmt);
    } else {
        // Case 3: Inspecie and InvAllocation
        let concatedAssetAllocations = _(investmentAllocation.inSpecieAssetAllocations)
            .concat(investmentAllocation.assetAllocations);
        assetAllocations = _.uniq(concatedAssetAllocations.value(),
            function(a) {
                return a.assetAllocationCode.concat(a.investmentOptionId);
            });
        consolidatedInvestmentOptions = _getConsldInvInspecieAndAlloc(inSpecieInvestmentOptions, invAllocationOptions, externalTotalRolloverAmt);
    }

    let consolidatedAssetAllocations = _.sortBy(assetAllocations, function(a) {
        return (AssetAllocationConstants.AssetClassCodeToOrder[a.assetAllocationCode].sortOrder);
    });
    _.forEach(consolidatedAssetAllocations, function(a) {
        _.assign(a, { displayName: AssetAllocationConstants.AssetClassCodeToOrder[a.assetAllocationCode].displayName });
    })

    // Rounding for display
    _.each(consolidatedInvestmentOptions,
        opt => {
            opt.weight = _.round(opt.weight, 2);
        });

    const sumOfWegiht = _.sum(consolidatedInvestmentOptions, 'weight');

    if (sumOfWegiht != 100) {
        const less = 100 - sumOfWegiht;
        if (less !== 0) {
            let lastIndex = _.last(consolidatedInvestmentOptions);
            if (lastIndex) {
                const addUpValue = lastIndex.weight + less;
                lastIndex.weight = _.round(addUpValue, 2);
            }
        }
    }

    if (forSubmit) {
        _.each(consolidatedInvestmentOptions,
        opt => {
            opt.weight = opt.weight.toFixed(2);
        });
    }

    const investmentOptionsExternalOnly = _getInvestmentOptionsWithAmount(externalTotalRolloverAmt, invAllocationOptions);
    const assetAllocationsExternalOnly = _getAssetAllocationWithDisplayNames(investmentAllocation.assetAllocations);

    return {
        consolidatedInSpecieOptions: consolidatedInSpecieOptions,
        consolidatedInvestmentOptions: consolidatedInvestmentOptions,
        consolidatedAssetAllocations: consolidatedAssetAllocations,
        investmentOptionsExternalOnly: investmentOptionsExternalOnly,
        assetAllocationsExternalOnly: assetAllocationsExternalOnly
    }
}

function calculateInvAllocations(fundingYourProduct, investmentAllocation, inSpecieTransfer, product, forSubmit) {
    if (!inSpecieTransfer || !Products.isPension(product)) {
        return _getConsolidatedDataForNonPension(fundingYourProduct.totalAmount, investmentAllocation.investmentOptions, investmentAllocation.assetAllocations);
    } else {
        return _getConsolidatedDataForPension(inSpecieTransfer, investmentAllocation, forSubmit);
    }
}

function getSubmissionFormModel(formModel, productId) {
    let consolidatedFormModel = _.cloneDeep(formModel);
    const inSpecieTransfer = _.get(consolidatedFormModel, 'form.inSpecieTransfer', null);
    const fundingYourProduct = _.get(consolidatedFormModel, 'form.fundingYourProduct', null);
    const investmentAllocation = _.get(consolidatedFormModel, 'form.investmentAllocation', null);

    if (inSpecieTransfer && fundingYourProduct && investmentAllocation) {
        const consolidatedInvestmentOptions = calculateInvAllocations(
            fundingYourProduct,
            investmentAllocation,
            inSpecieTransfer,
            productId,
            true);
        consolidatedFormModel.form.investmentAllocation.investmentOptions = consolidatedInvestmentOptions.consolidatedInvestmentOptions;
    }

    return ApplicationHelper.getApplicationWithoutPredictiveSearchAdviceFee(consolidatedFormModel);
}

const ConsolidateInvestmentOptionsHelper = {
    calculateInvAllocations: calculateInvAllocations,
    getSubmissionFormModel: getSubmissionFormModel
};

export default ConsolidateInvestmentOptionsHelper;


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./utils/ConsolidateInvestmentOptionsHelper.js